/**
 * The search snippet is UNTRUSTED SERVER HTML (`070-search-and-links.md` §5).
 *
 * `ts_headline` wraps the matched terms in `<mark>`, and **the surrounding text is raw
 * page content** — in a notebook where people paste from the web, a page really can
 * contain `<img src=x onerror=alert(1)>`, and Postgres will happily copy it into the
 * snippet. The server escapes it (`040-api-contract.md`), but that is the first line of
 * defence, not the only one.
 *
 * So: **the snippet never becomes an HTML string on the client.** It is tokenized here
 * into a flat array, and `SearchSnippet.tsx` renders those tokens as React elements, which
 * escapes every string child. There is no HTML parsing step and therefore no sanitizer
 * bypass to get wrong. The allowlist is exactly one element — `<mark>` — with zero attrs,
 * and it is enforced by the fact that this function cannot emit anything else.
 *
 * `dangerouslySetInnerHTML` is not used here, is not used in the component, and must never
 * be introduced into either.
 */

/** `StartSel`/`StopSel` are fixed to `<mark>`/`</mark>` server-side, so this is exhaustive. */
const MARK = /<mark>([\s\S]*?)<\/mark>/g;

export interface SnippetToken {
  /** `mark` renders inside a `<mark>`; `text` renders as a bare (React-escaped) string. */
  kind: 'text' | 'mark';
  value: string;
}

/**
 * Split a server snippet into text/mark tokens.
 *
 * Everything that is not a well-formed `<mark>…</mark>` pair — including a lone `<mark>`,
 * a `<script>`, an attribute, or any other tag — falls through as literal TEXT, and text
 * is escaped by React on render. That is the whole security property: the only way to
 * produce an element from this function is to be a `<mark>` pair, and the only element it
 * can produce is a `<mark>`.
 */
export function parseSnippet(html: string): SnippetToken[] {
  const tokens: SnippetToken[] = [];
  let last = 0;

  for (const match of html.matchAll(MARK)) {
    const at = match.index;

    if (at > last) tokens.push({ kind: 'text', value: html.slice(last, at) });
    tokens.push({ kind: 'mark', value: match[1] ?? '' });

    last = at + match[0].length;
  }

  if (last < html.length) tokens.push({ kind: 'text', value: html.slice(last) });

  return tokens;
}
